import { useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { useSnackbar } from 'notistack'
import { useTranslation } from 'react-i18next'
import { Control, SubmitHandler, useForm, useWatch } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'

import { LoadingButton } from '@mui/lab'
import { Avatar, Box, Drawer, IconButton, List, ListItem, ListItemAvatar, ListItemText, Stack, Toolbar, Typography } from '@mui/material'
import AccountCircleOutlinedIcon from '@mui/icons-material/AccountCircleOutlined'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'

import QuillController from '@/components/ui/inputs/QuillController'
import SelectInputController from '@/components/ui/inputs/SelectInputController'
import TextInputController from '@/components/ui/inputs/TextInputController'

import { useCreateNetworkMutation } from '@/core/services/network'

import { useIsMobile } from '@/hooks/useSizeBreakpoints'

import { MAIN_HEADER_HEIGHT } from '@/layouts/main'

import { getApiErrorMessage } from '@/utils/errors'

import NetworkPreview from './components/NetworkPreview'

const CreateNetworkFormSchema = z.object({
  description: z.string().optional(),
  name: z.string().min(1),
  privacy: z.enum(['public', 'private']),
})

export type CreateNetworkFormValues = z.infer<typeof CreateNetworkFormSchema>

export default function CreateNetworkPage() {
  const navigate = useNavigate()
  const { enqueueSnackbar } = useSnackbar()
  const { t } = useTranslation()
  const isMobile = useIsMobile()

  const [createNetwork, { isLoading: isCreatingNetwork }] = useCreateNetworkMutation()

  const { control, handleSubmit } = useForm<CreateNetworkFormValues>({
    defaultValues: { description: '', name: '', privacy: 'public' },
    resolver: zodResolver(CreateNetworkFormSchema),
  })

  const onSubmit: SubmitHandler<CreateNetworkFormValues> = async (values) => {
    try {
      const network = await createNetwork({
        description: values.description,
        name: values.name,
        privacy: values.privacy as 'public' | 'private',
      }).unwrap()
      navigate(`/networks/${network._id}`)
    } catch (error) {
      enqueueSnackbar(getApiErrorMessage(error, t('notification.network.create.error')), { variant: 'error' })
    }
  }

  const formContent = (
    <>
      <NetworkHeader />
      <CreateNetworkForm control={control} />
      <Box pt={2} px={2}>
        <SubmitButton isLoading={isCreatingNetwork} />
      </Box>
    </>
  )

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      {isMobile ? (
        <Stack direction="column" flex={1} pb={6}>
          {formContent}
        </Stack>
      ) : (
        <Stack direction="row" flex={1} sx={{ height: `calc(100vh - ${MAIN_HEADER_HEIGHT}px)` }}>
          <Drawer
            variant="permanent"
            sx={{
              width: 360,
              flexShrink: 0,
              [`& .MuiDrawer-paper`]: {
                width: 360,
                boxSizing: 'border-box',
                pb: '20px',
                position: 'absolute',
              },
            }}
          >
            <Toolbar sx={{ minHeight: 'unset !important', height: MAIN_HEADER_HEIGHT }} />
            {formContent}
          </Drawer>
          <Box flex={1} sx={{ overflowY: 'auto' }}>
            <Box mx={3} mt={7} mb={4}>
              <Preview control={control} />
            </Box>
          </Box>
        </Stack>
      )}
    </form>
  )
}

function CreateNetworkForm({ control }: { control: Control<CreateNetworkFormValues> }) {
  const { t } = useTranslation()
  const privacyOptions = useMemo(
    () => [
      { label: 'Public', value: 'public' },
      { label: 'Private', value: 'private' },
    ],
    []
  )
  return (
    <Stack flex={1}>
      <List sx={{ width: '100%', maxWidth: 360, bgcolor: 'background.paper' }}>
        <ListItem>
          <ListItemAvatar>
            <Avatar>
              <AccountCircleOutlinedIcon />
            </Avatar>
          </ListItemAvatar>
          <ListItemText primary="Photos" secondary="Jan 9, 2014" />
        </ListItem>
      </List>
      <Stack p={2} spacing={2}>
        <TextInputController control={control} label={t('field.name.label')} name="name" />
        <div>
          <QuillController<CreateNetworkFormValues> control={control} label={t('field.description.label')} name="description" />
        </div>
        <SelectInputController control={control} label={t('field.privacy.label')} name="privacy" options={privacyOptions} />
      </Stack>
    </Stack>
  )
}

function NetworkHeader() {
  const { t } = useTranslation()
  const isMobile = useIsMobile()
  const navigate = useNavigate()

  return (
    <Box mt="20px" mx={2} mb="12px">
      <Stack direction="row" alignItems="center" justifyContent="flex-start" spacing={3}>
        {!isMobile && (
          <IconButton
            id="previous-button"
            data-testid="previous-button"
            onClick={() => {
              navigate('/networks/joins')
            }}
            size="small"
            sx={{ mt: '10px' }}
          >
            <ArrowBackIcon color="primary" fontSize="small" />
          </IconButton>
        )}
        <Typography component="h1" sx={{ fontSize: 24, fontWeight: 700, lineHeight: '28px' }}>
          {t('page.networks.title')}
        </Typography>
      </Stack>
    </Box>
  )
}

function SubmitButton({ isLoading }: { isLoading: boolean }) {
  const { t } = useTranslation()
  return (
    <LoadingButton fullWidth loading={isLoading} variant="contained" type="submit">
      {t('common:button.create.label')}
    </LoadingButton>
  )
}

function Preview({ control }: { control: Control<CreateNetworkFormValues> }) {
  const [description, name, privacy] = useWatch({ control, name: ['description', 'name', 'privacy'] })
  return <NetworkPreview name={name} privacy={privacy} description={description ?? ''} />
}
